import {type BskyAgent} from '@atproto/api'
import {type I18n} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {AbortError} from '#/lib/async/cancelable'
import {BSKY_SERVICE} from '#/lib/constants'
import {DOCUMENT_MAX_SIZE} from '#/lib/document/limits'
import {logger} from '#/logger'

export type DocumentFile = {
  name: string
  size: number
  mimeType: string
  uri: string
  bytes?: Uint8Array
  /**
   * Pages read by the picker. The optimizer re-parses its own output and
   * compares the two, so a compressed file that lost a page is never used.
   */
  pageCount?: number
}

export type DocumentPendingPublish = {
  blob: {
    $type: 'blob'
    ref: {$link: string}
    mimeType: string
    size: number
  }
  mimeType: string
  pageCount: number
  fileSize: number
  title: string
}

export type DocumentAction =
  | {type: 'update_progress'; progress: number; signal: AbortSignal}
  | {
      type: 'to_done'
      pendingPublish: DocumentPendingPublish
      signal: AbortSignal
    }
  | {type: 'to_error'; error: string; signal: AbortSignal}

type UploadingState = {
  status: 'uploading'
  progress: number
  abortController: AbortController
  file: DocumentFile
  pendingPublish?: undefined
  error?: undefined
}

type DoneState = {
  status: 'done'
  progress: 100
  abortController: AbortController
  file: DocumentFile
  pendingPublish: DocumentPendingPublish
  error?: undefined
}

type ErrorState = {
  status: 'error'
  progress: 100
  abortController: AbortController
  file: DocumentFile
  pendingPublish?: undefined
  error: string
}

export type DocumentState = UploadingState | DoneState | ErrorState

export function createDocumentState(
  file: DocumentFile,
  abortController: AbortController,
): UploadingState {
  return {
    status: 'uploading',
    progress: 0,
    abortController,
    file,
  }
}

export function documentReducer(
  state: DocumentState,
  action: DocumentAction,
): DocumentState {
  if (action.signal.aborted || action.signal !== state.abortController.signal) {
    return state
  }
  if (action.type === 'to_error') {
    return {
      status: 'error',
      progress: 100,
      abortController: state.abortController,
      file: state.file,
      error: action.error,
    }
  } else if (action.type === 'update_progress') {
    if (state.status === 'uploading') {
      return {...state, progress: action.progress}
    }
  } else if (action.type === 'to_done') {
    return {
      status: 'done',
      progress: 100,
      abortController: state.abortController,
      file: state.file,
      pendingPublish: action.pendingPublish,
    }
  }
  return state
}

interface DocumentBlob {
  $type: 'blob'
  ref: {$link: string}
  mimeType: string
  size: number
}

interface DocumentJobStatus {
  jobId: string
  did: string
  state: string
  blob: DocumentBlob | null
  mimeType?: string
  pageCount?: number
  fileSize?: number
  error?: string
}

/**
 * How long an upload may make no progress before we stop waiting on it. A
 * request that stalls mid-flight has no other way out — without this the
 * composer would spin on "Uploading…" forever.
 */
export const UPLOAD_STALL_TIMEOUT_MS = 60_000

function readDocumentBytes(
  file: DocumentFile,
  signal: AbortSignal,
): Promise<Uint8Array> {
  if (file.bytes) return Promise.resolve(file.bytes)
  // Passing the signal keeps a document the user removed mid-read from
  // continuing to pull bytes down in the background.
  return fetch(file.uri, {signal})
    .then(res => res.arrayBuffer())
    .then(buffer => new Uint8Array(buffer))
}

function uploadErrorMessage(responseText: string): string | undefined {
  try {
    const parsed = JSON.parse(responseText) as {message?: unknown}
    const message = parsed?.message
    return typeof message === 'string' && message ? message : undefined
  } catch {
    return undefined
  }
}

/**
 * Uploads the bytes and resolves with the server's job status. The promise is
 * guaranteed to settle exactly once: success, HTTP error, network error, abort
 * and stall all funnel through `finish`. A promise that never settles is what
 * leaves a document stuck at "Uploading…", because the reducer never sees a
 * terminal action.
 */
function uploadDocument({
  bytes,
  file,
  agent,
  signal,
  onProgress,
  _,
}: {
  bytes: Uint8Array
  file: DocumentFile
  agent: BskyAgent
  signal: AbortSignal
  onProgress: (progress: number) => void
  _: I18n['_']
}): Promise<{jobStatus: DocumentJobStatus}> {
  const uploadUrl = `${BSKY_SERVICE}/xrpc/app.bsky.document.uploadDocument`
  // Rendered up front, on purpose: building a message inside an XHR event
  // handler is too late to reject the promise, and a throw there is exactly
  // how a document ends up stuck at "Uploading…" for good.
  const genericMessage = _(msg`Failed to upload document`)
  const tooLargeMessage = _(
    msg`Documents must be under ${Math.round(
      DOCUMENT_MAX_SIZE / (1024 * 1024),
    )} MB`,
  )
  const stalledMessage = _(
    msg`The upload stalled. Check your connection and try again.`,
  )

  return new Promise<{jobStatus: DocumentJobStatus}>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let settled = false
    let stallTimer: ReturnType<typeof setTimeout> | undefined

    const finish = (settle: () => void) => {
      if (settled) return
      settled = true
      if (stallTimer !== undefined) clearTimeout(stallTimer)
      signal.removeEventListener('abort', onAbort)
      settle()
    }
    const fail = (message: string) => finish(() => reject(new Error(message)))
    const armStallTimer = () => {
      if (stallTimer !== undefined) clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        xhr.abort()
        fail(stalledMessage)
      }, UPLOAD_STALL_TIMEOUT_MS)
    }
    function onAbort() {
      xhr.abort()
      finish(() => reject(new AbortError()))
    }

    try {
      signal.addEventListener('abort', onAbort)
      armStallTimer()

      xhr.upload.addEventListener('progress', e => {
        armStallTimer()
        if (e.lengthComputable) onProgress(e.loaded / e.total)
      })

      // Everything below runs inside an event handler, where a stray throw can
      // no longer reject the promise — hence the try/catch inside.
      xhr.onloadend = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          finish(() => {
            try {
              resolve(
                JSON.parse(xhr.responseText) as {jobStatus: DocumentJobStatus},
              )
            } catch {
              reject(new Error(genericMessage))
            }
          })
        } else if (xhr.status === 413) {
          // The server rejects oversized bodies before the handler runs, so its
          // "request entity too large" says nothing about the limit.
          fail(tooLargeMessage)
        } else {
          fail(uploadErrorMessage(xhr.responseText) ?? genericMessage)
        }
      }
      xhr.onerror = () => fail(genericMessage)

      xhr.open('POST', uploadUrl)
      xhr.setRequestHeader('Content-Type', file.mimeType)
      xhr.setRequestHeader(
        'Authorization',
        `Bearer ${agent.session?.accessJwt}`,
      )
      xhr.send(bytes)
    } catch (e) {
      // `send` throws synchronously for a few states (bad URL, detached body),
      // which used to escape as an unhandled rejection.
      finish(() => reject(e instanceof Error ? e : new Error(genericMessage)))
    }
  })
}

export async function processDocument(
  file: DocumentFile,
  dispatch: (action: DocumentAction) => void,
  agent: BskyAgent,
  signal: AbortSignal,
  _: I18n['_'],
) {
  if (signal.aborted) return

  // `_` is a prototype method that reads `this.locale`, so a translator handed
  // over detached throws instead of returning text. This string is what reports
  // the failure below, so it has to survive that.
  let failureMessage = 'Failed to upload document'
  try {
    failureMessage = _(msg`Failed to upload document`)
  } catch {}

  try {
    const bytes = await readDocumentBytes(file, signal)
    if (signal.aborted) return

    const {jobStatus} = await uploadDocument({
      bytes,
      file,
      agent,
      signal,
      _,
      onProgress: progress =>
        dispatch({type: 'update_progress', progress, signal}),
    })

    if (signal.aborted) return

    if (jobStatus.state === 'JOB_STATE_FAILED') {
      throw new Error(jobStatus.error ?? _(msg`Document upload failed`))
    }

    if (!jobStatus.blob) {
      throw new Error(_(msg`Document upload failed: no blob returned`))
    }

    // Filenames frequently carry stray whitespace (" deck.pdf"), and a file
    // named just `.pdf` would leave nothing behind, so trim and fall back to a
    // readable default rather than publishing a blank or padded title.
    const title = file.name.replace(/\.[^.]+$/, '').trim()

    const pendingPublish: DocumentPendingPublish = {
      blob: jobStatus.blob,
      mimeType: jobStatus.mimeType ?? file.mimeType,
      pageCount: jobStatus.pageCount ?? 0,
      fileSize: jobStatus.fileSize ?? file.size,
      title: title || _(msg`Document`),
    }
    dispatch({type: 'to_done', pendingPublish, signal})
  } catch (e) {
    // Every failure has to end in a terminal state, otherwise the document
    // sits at "Uploading…" for good and the only way out is removing it.
    if (e instanceof AbortError || signal.aborted) return
    logger.error('Error uploading document', {safeMessage: e})
    dispatch({
      type: 'to_error',
      error: e instanceof Error ? e.message : failureMessage,
      signal,
    })
  }
}
